import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  AfterViewInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

type KeyboardKey = string | { key: string; colSpan: number };

const COL_SPAN_CLASSES: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  6: 'col-span-6',
  10: 'col-span-10',
};

@Component({
  selector: 'app-kiosk-virtual-keyboard',
  templateUrl: './kiosk-virtual-keyboard.component.html',
  standalone: false,
})
export class KioskVirtualKeyboardComponent implements OnDestroy, AfterViewInit {
  @Input() backgroundColor?: string;
  @Input() textColor?: string;
  @Input() isFirstTouch?: boolean = true;
  @Input() onlyUpperCase?: boolean = false;
  @Input() isOverFooter?: boolean = false;
  /** `numeric` is the digit pad used by funnel fields such as a table number. */
  @Input() inputCharset: 'text' | 'numeric' = 'text';

  @Output() keyPress = new EventEmitter<string>();
  @Output() onBlurEvent = new EventEmitter();

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.stopPropagation();
  }

  private documentClickListener?: () => void;

  firstclick: boolean = true;

  private readonly textKeyboardLayout: KeyboardKey[][] = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
    [
      { key: this.translateService.instant('kiosk-virtual-keyboard.Backspace'), colSpan: 2 },
      'w',
      'x',
      'c',
      'v',
      'b',
      'n',
      { key: this.translateService.instant('kiosk-virtual-keyboard.Enter'), colSpan: 2 },
    ],
  ];

  private readonly numericKeyboardLayout: KeyboardKey[][] = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [
      { key: this.translateService.instant('kiosk-virtual-keyboard.Backspace'), colSpan: 1 },
      '0',
      { key: this.translateService.instant('kiosk-virtual-keyboard.Enter'), colSpan: 1 },
    ],
  ];

  get keyboardLayout(): KeyboardKey[][] {
    return this.inputCharset === 'numeric' ? this.numericKeyboardLayout : this.textKeyboardLayout;
  }

  get showSpaceRow(): boolean {
    return this.inputCharset !== 'numeric';
  }

  get layoutGridClass(): string {
    return this.inputCharset === 'numeric' ? 'grid-cols-3' : 'grid-cols-10';
  }

  // Email-related chars sit on the space row so users can type addresses
  // without adding a full extra square row (kiosk screens are already tight).
  spaceBarLayout: KeyboardKey[] = [
    { key: '@', colSpan: 1 },
    { key: '.', colSpan: 1 },
    { key: ' ', colSpan: 6 },
    { key: '-', colSpan: 1 },
    { key: '_', colSpan: 1 },
  ];

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
    private readonly translateService: TranslateService,
  ) {}

  ngAfterViewInit() {
    this.cd.detectChanges();
    this.documentClickListener = this.renderer.listen('document', 'click', event => {
      if (this.firstclick) {
        this.firstclick = false;
        return;
      }

      if (!this.el.nativeElement.contains(event.target)) {
        this.onBlurEvent.emit();
      }
    });
  }

  ngOnDestroy() {
    if (this.documentClickListener) {
      this.documentClickListener();
    }
  }

  onKeyPress(key: string) {
    this.isFirstTouch = false;
    this.keyPress.emit(key);
  }

  isSpecialKey(item: KeyboardKey) {
    return typeof item !== 'string';
  }

  getKeyValue(item: KeyboardKey) {
    return typeof item === 'object' ? item.key : item;
  }

  getDisplayValue(item: KeyboardKey) {
    const keyValue = this.getKeyValue(item);
    // A digit pad has no shift state: keep Corriger / Entrer as translated.
    if (this.inputCharset === 'numeric') {
      return keyValue;
    }
    return this.isFirstTouch || this.onlyUpperCase ? keyValue.toUpperCase() : keyValue;
  }

  getAspectClass(item: KeyboardKey) {
    if (this.inputCharset === 'numeric' || typeof item !== 'string') {
      return '';
    }
    return 'aspect-square';
  }

  getColSpanClass(item: KeyboardKey) {
    if (typeof item === 'string') {
      return 'col-span-1';
    }
    return COL_SPAN_CLASSES[item.colSpan] ?? 'col-span-1';
  }
}
